import React, { useCallback, useState } from "react";
import { FiMenu, FiPlus, FiX } from "react-icons/fi";
import ChatComponent from "../components/ai/ChatComponent";
import ChatHistory from "../components/ai/ChatHistory";
import { ChatHistoryProvider } from "../contexts/ChatHistoryContext";

const STARTER_PROMPTS = [
  "Which acquirers is the Ingenico Self 2000 certified with?",
  "What are the e-Commerce capabilities that TNS supports?",
  "Which terminals would be best for the Parking industry?",
  "Which terminals are best for fuel payments?",
  "Can you explain TNS's fraud prevention capabilities?",
  "What industries does TNS serve?",
  "What is Complete Commerce?",
  "What is Hosted Checkout?",
  "Summarize TNS' payment capabilities",
  "Generate a 2-minute elevator pitch",
  "Why choose TNS over competitors",
];

/**
 * The /ask page.
 *
 * A chat application shell rather than a scrolling marketing page: the rail
 * and composer hold still and only the transcript moves. The hero and starter
 * prompts now live inside the empty state, so they get out of the way once
 * there is a conversation to read.
 */
export default function Home() {
  const [question, setQuestion] = useState("");
  // Bumped on every selection so picking the same prompt or rail entry twice
  // in a row asks it again instead of being swallowed as an unchanged prop.
  const [questionNonce, setQuestionNonce] = useState(0);
  const [resetSignal, setResetSignal] = useState(0);
  const [railOpen, setRailOpen] = useState(false);
  // Which saved conversation to open, and a nonce so re-selecting the same one
  // reloads it rather than being swallowed as an unchanged prop.
  const [loadThreadId, setLoadThreadId] = useState(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Starter prompts and the composer ask a NEW question.
  const askQuestion = useCallback((text) => {
    setQuestion(text);
    setQuestionNonce((n) => n + 1);
    setRailOpen(false);
  }, []);

  // The rail OPENS a saved conversation, which is a different action entirely.
  //
  // This used to call askQuestion(text, threadId), which set the thread id and
  // then dispatched the question anyway — so clicking a recent question asked it
  // again inside that conversation. Repeated clicks stacked duplicate turns, and
  // the answer you were trying to get back to was never shown. The thread is
  // loaded now and nothing is sent.
  const openThread = useCallback((_text, threadId) => {
    if (!threadId) return;
    setLoadThreadId(threadId);
    setLoadNonce((n) => n + 1);
    setRailOpen(false);
  }, []);

  const startNewChat = useCallback(() => {
    setResetSignal((n) => n + 1);
    setQuestion("");
    setRailOpen(false);
  }, []);

  return (
    <ChatHistoryProvider>
      <div className="iq-chat iq-shell flex overflow-hidden bg-iq-canvas font-noto-sans text-iq-ink">
        <aside className="hidden w-[264px] shrink-0 border-r border-iq-line md:block">
          <ChatHistory onQuestionSelect={openThread} onNewChat={startNewChat} />
        </aside>

        {railOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <button
              type="button"
              aria-label="Close recent questions"
              onClick={() => setRailOpen(false)}
              className="absolute inset-0 h-full w-full bg-black/30"
            />
            <aside className="absolute left-0 top-0 h-full w-[282px] border-r border-iq-line shadow-xl">
              <button
                type="button"
                aria-label="Close recent questions"
                onClick={() => setRailOpen(false)}
                className="absolute right-2 top-2 z-10 rounded-md p-1.5 text-iq-ink-faint hover:bg-white hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
              >
                <FiX className="h-4 w-4" />
              </button>
              <ChatHistory
                onQuestionSelect={openThread}
                onNewChat={startNewChat}
              />
            </aside>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center gap-2 border-b border-iq-line px-3 py-2 md:hidden">
            <button
              type="button"
              onClick={() => setRailOpen(true)}
              aria-label="Open recent questions"
              className="rounded-md p-1.5 text-iq-ink-soft hover:bg-iq-rail hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
            >
              <FiMenu className="h-4 w-4" />
            </button>
            <span className="text-[13px] font-semibold text-iq-ink">
              TNSPay IQ
            </span>
            <button
              type="button"
              onClick={startNewChat}
              className="ml-auto flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12.5px] font-medium text-iq-ink-soft hover:bg-iq-rail hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
            >
              <FiPlus className="h-3.5 w-3.5" />
              New
            </button>
          </div>

          <ChatComponent
            layout="fullheight"
            persistKey="ask"
            question={question}
            questionNonce={questionNonce}
            loadThreadId={loadThreadId}
            loadNonce={loadNonce}
            resetSignal={resetSignal}
            starterPrompts={STARTER_PROMPTS}
          />
        </div>
      </div>
    </ChatHistoryProvider>
  );
}
